import { useRef, type ClipboardEvent, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { cx } from "./ui";

const LENGTH = 6;

/** Six single-digit boxes. Supports typing, backspace, arrows and pasting a whole code. */
export function OtpInput({
  value,
  onChange,
  onComplete,
  disabled,
  invalid,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  onComplete?: (v: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length: LENGTH }, (_, i) => value[i] ?? "");

  const focus = (i: number) => refs.current[Math.max(0, Math.min(LENGTH - 1, i))]?.focus();

  const commit = (next: string, focusIndex: number) => {
    const clean = next.replace(/\D/g, "").slice(0, LENGTH);
    onChange(clean);
    focus(focusIndex);
    if (clean.length === LENGTH) onComplete?.(clean);
  };

  const onInput = (i: number, raw: string) => {
    const d = raw.replace(/\D/g, "");
    if (!d) return;
    if (d.length > 1) {
      // Autofill (e.g. one-time-code from the OS) can drop the whole code into one box.
      commit(d, d.length);
      return;
    }
    // The value has no gaps: typing past the end appends.
    const at = Math.min(i, value.length);
    commit(value.slice(0, at) + d + value.slice(at + 1), at + 1);
  };

  const onKeyDown = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (value[i]) {
        onChange(value.slice(0, i) + value.slice(i + 1));
      } else if (i > 0) {
        onChange(value.slice(0, i - 1) + value.slice(i));
        focus(i - 1);
      }
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focus(i - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focus(i + 1);
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const d = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!d) return;
    e.preventDefault();
    commit(d, Math.min(d.length, LENGTH - 1));
  };

  return (
    <div className="flex gap-2">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          value={d}
          onChange={(e) => onInput(i, e.target.value)}
          onKeyDown={(e) => onKeyDown(i, e)}
          onPaste={onPaste}
          onFocus={(e) => e.target.select()}
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={i === 0 ? LENGTH : 1}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          aria-label={t("auth.digit", { n: i + 1 })}
          aria-invalid={invalid || undefined}
          className={cx(
            "h-[52px] w-11 rounded-[var(--radius-ctl)] border bg-surface text-center font-mono text-[22px] font-semibold",
            "focus:border-accent focus:shadow-[0_0_0_3px_var(--accent-soft)] focus:outline-none",
            invalid ? "border-fail-fg" : "border-border-strong",
          )}
        />
      ))}
    </div>
  );
}
